import { Check, ChevronsUpDown, LoaderCircle, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge,
    Button,
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    Label,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Text,
} from '@/ui/design-system/components';
import {
    SEARCH_DEBOUNCE_MS,
    createProxySearchClient,
    searchEntities,
} from './entitySearch';
import { useEntities } from 'utils/hooks/api';

export const MAX_PINNED = 20;

const PINNED_COPY = {
    brand: {
        label: 'Specific brands',
        noun: 'brand',
        nounPlural: 'brands',
        types: ['brand'],
        apiType: 'brands',
    },
    category: {
        label: 'Specific categories',
        noun: 'category',
        nounPlural: 'categories',
        types: ['category'],
        apiType: 'categories',
    },
};

function WidgetEntityMultiSelect({
    value,
    pinnedKind,
    dataSource,
    onChange,
    id,
    label,
    excludeDepths,
    required = false,
    maxItems = MAX_PINNED,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [pickedNames, setPickedNames] = useState({});
    const copy = PINNED_COPY[pinnedKind] ?? PINNED_COPY.category;
    const client = useMemo(() => createProxySearchClient(), []);
    const reqIdRef = useRef(0);

    useEffect(() => {
        const q = query.trim();
        if (!q) {
            reqIdRef.current += 1;
            setHits([]);
            setIsSearching(false);
            return undefined;
        }
        setIsSearching(true);
        const reqId = ++reqIdRef.current;
        const timer = setTimeout(async () => {
            try {
                const found = await searchEntities({
                    client,
                    query: q,
                    types: copy.types,
                    excludeDepths,
                });
                if (reqId !== reqIdRef.current) return; // stale
                setHits(found);
            } catch {
                if (reqId === reqIdRef.current) setHits([]);
            } finally {
                if (reqId === reqIdRef.current) setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query, client, copy.types, excludeDepths]);

    const { data: hydrated } = useEntities({
        ids: value,
        entityType: copy.apiType,
        dataSource,
        enabled: value.length > 0,
    });
    const hydratedNameById = useMemo(() => {
        const names = {};
        for (const entity of hydrated ?? []) names[entity.id] = entity.name;
        return names;
    }, [hydrated]);
    const nameFor = (entityId) =>
        hydratedNameById[entityId] ?? pickedNames[entityId] ?? '…';

    const emit = (ids) => onChange([...new Set(ids)].sort());

    const remove = (entityId) => emit(value.filter((v) => v !== entityId));

    const toggle = (hit) => {
        const hitId = hit.id ?? hit.objectID;
        if (value.includes(hitId)) {
            remove(hitId);
            return;
        }
        if (value.length >= maxItems) return;
        setPickedNames((prev) => ({ ...prev, [hitId]: hit.name }));
        emit([...value, hitId]);
    };

    const atLimit = value.length >= maxItems;
    const fieldId = id ?? 'widget-pinned-entities';

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
                <Label
                    htmlFor={fieldId}
                    className="text-sm font-medium text-foreground"
                >
                    {label ?? copy.label}
                </Label>
                <Text size="xs" color="muted">
                    {required
                        ? `Pick the ${copy.nounPlural} to show.`
                        : `Show these ${copy.nounPlural} or leave empty for top results.`}
                </Text>
            </div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={fieldId}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span
                            className={
                                value.length
                                    ? 'truncate'
                                    : 'text-muted-foreground'
                            }
                        >
                            {value.length
                                ? `${value.length} selected`
                                : `Search for ${copy.nounPlural}…`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={`Search ${copy.nounPlural}…`}
                            value={query}
                            onValueChange={setQuery}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {isSearching ? (
                                    <span className="flex items-center justify-center gap-2 text-muted-foreground">
                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                        Searching…
                                    </span>
                                ) : query.trim() ? (
                                    'No matches.'
                                ) : (
                                    `Type to search ${copy.nounPlural}.`
                                )}
                            </CommandEmpty>
                            {hits.map((hit) => {
                                const hitId = hit.id ?? hit.objectID;
                                const isPinned = value.includes(hitId);
                                return (
                                    <CommandItem
                                        key={hitId}
                                        value={`${hit.name}-${hitId}`}
                                        disabled={!isPinned && atLimit}
                                        onSelect={() => toggle(hit)}
                                    >
                                        <Check
                                            className={`mr-2 h-4 w-4 ${
                                                isPinned
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                            }`}
                                        />
                                        <span className="flex flex-col">
                                            <span className="capitalize">
                                                {hit.name}
                                            </span>
                                            <span className="text-[10px] capitalize text-muted-foreground">
                                                {hit.type}
                                            </span>
                                        </span>
                                    </CommandItem>
                                );
                            })}
                            {atLimit && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                    Up to {maxItems} {copy.nounPlural} can be
                                    selected.
                                </div>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((entityId) => (
                        <Badge
                            key={entityId}
                            variant="secondary"
                            interactive
                            className="h-6 px-1.5"
                            aria-label={`Remove ${nameFor(entityId)}`}
                            onClick={() => remove(entityId)}
                        >
                            <span className="text-xs capitalize">
                                {nameFor(entityId)}
                            </span>
                            <X className="h-3 w-3 text-muted-foreground" />
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WidgetEntityMultiSelect;
